import { OrganisationsMetadataRoutes, getOrgMetadataRoutes } from '@auth/contexts/getOrgMetadataRoutes';
import { User, useAuth0 } from '@auth0/auth0-react';
import { Message, useBroadcastChannelReceiver } from '@ocula/broadcast-channel';
import { ReactNode, createContext, useCallback, useContext, useMemo } from 'react';

export interface AuthContextType {
  isLoading: boolean;
  isAuthenticated: boolean;
  // biome-ignore lint/suspicious/noExplicitAny: TODO: fix
  error?: any;
  user?: User;
  organisations: OrganisationsMetadataRoutes;
  logout: ReturnType<typeof useAuth0>['logout'];
  loginWithRedirect: ReturnType<typeof useAuth0>['loginWithRedirect'];
  getAccessTokenSilently: ReturnType<typeof useAuth0>['getAccessTokenSilently'];
}

export const AuthContext = createContext<AuthContextType>({
  isLoading: true,
  isAuthenticated: false,
  organisations: [],
  logout: () => {},
  loginWithRedirect: () => Promise.reject(new Error('Not implemented')),
  // biome-ignore lint/suspicious/noExplicitAny: TODO: fix
  getAccessTokenSilently: () => Promise.reject<any>(new Error('Not implemented')),
});

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Wraps the Auth0 state and functions along with our own authorization data
 * for the authenticated user to create a single, unified context that can be
 * accessed using the `useAuth` hook.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const { isLoading, isAuthenticated, error, user, logout, loginWithRedirect, getAccessTokenSilently } = useAuth0();

  const meta = user && getOrgMetadataRoutes(user);

  // Listen for logout messages from any other open tabs that are running this app too.
  const logoutMessageHandler = useCallback(
    (message: Message) => {
      if (message.type === 'logout') {
        logout();
      }
    },
    [logout],
  );
  useBroadcastChannelReceiver(logoutMessageHandler);

  const contextValue = useMemo(
    () => ({
      isLoading,
      isAuthenticated,
      error: error ?? (meta?.kind === 'error' && meta.error) ?? undefined,
      user,
      logout,
      loginWithRedirect,
      getAccessTokenSilently,
      organisations: meta?.kind === 'success' ? meta.data : [],
    }),
    [isLoading, isAuthenticated, error, user, logout, loginWithRedirect, getAccessTokenSilently, meta],
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}
